import apiClient, { API_BASE_URL } from '../api/apiClient';

import { formatToKSTDateString } from '../../utils/dateUtils';

// JWT 토큰 가져오기 함수
const getAuthToken = (): string | null => {
  return localStorage.getItem('access_token');
};

// HTTP 요청 헤더 생성
const getHeaders = (): HeadersInit => {
  const token = getAuthToken();
  return {
    'Content-Type': 'application/json',
    ...(token && { 'Authorization': `Bearer ${token}` }),
  };
};

// 커피챗 질문글 데이터 인터페이스 (API 문서에 맞게 수정)
export interface CoffeeChatQuestionData {
  id: number;
  title: string;
  content?: string | null;
  mentor_name: string;
  writer_name: string;
  created_at: string;
  visible: boolean;
  is_answered: boolean; // 새로 추가된 필드
  answer_id?: number | null;
  answer_content?: string | null;
  answer_writer_name?: string | null;
  answer_created_at?: string | null;
}

// 커피챗 질문글 목록 응답 인터페이스
export interface CoffeeChatQuestionListResponse {
  code: number;
  status: string;
  message: string;
  data: {
    totalElements: number;
    totalPages: number;
    size: number;
    content: CoffeeChatQuestionData[];
    number: number;
    sort: {
      empty: boolean;
      sorted: boolean;
      unsorted: boolean;
    };
    first: boolean;
    last: boolean;
    numberOfElements: number;
    pageable: {
      offset: number;
      sort: {
        empty: boolean;
        sorted: boolean;
        unsorted: boolean;
      };
      paged: boolean;
      pageNumber: number;
      pageSize: number;
      unpaged: boolean;
    };
    empty: boolean;
  };
}

// 커피챗 질문글 액션 응답 인터페이스
export interface CoffeeChatQuestionActionResponse {
  code: number;
  status: string;
  message: string;
  data: CoffeeChatQuestionData;
}

// 커피챗 질문글 삭제 응답 인터페이스
export interface CoffeeChatQuestionDeleteResponse {
  code: number;
  status: string;
  message: string;
  data: {};
}

// 필터 상태 타입 (isAnswered 필터 추가)
export interface CoffeeChatFilterState {
  mentorId: number | null;
  fromDate: string;
  toDate: string;
  visible: boolean | null; // true: 노출, false: 숨김, null: 전체
  isAnswered: boolean | null; // true: 답변완료, false: 답변대기, null: 전체
  searchTerm: string; // 검색어 추가
}

// 페이지네이션 상태 타입
export interface CoffeeChatPaginationState {
  page: number;
  size: number;
  totalPages: number;
  totalElements: number;
}

// API 함수들
export const adminCoffeeChatApi = {
  // 커피챗 질문글 목록 조회 (API 문서에 맞게 구현)
  getCoffeeChatQuestions: async (
    filter: CoffeeChatFilterState,
    pagination: { page: number; size: number }
  ): Promise<CoffeeChatQuestionListResponse> => {
    const params = new URLSearchParams();
    
    // 필수 파라미터
    params.append('page', pagination.page.toString());
    params.append('size', pagination.size.toString());

    // 선택적 파라미터들 (API 문서 순서대로)
    if (filter.visible !== null) {
      params.append('visible', filter.visible.toString());
    }
    
    if (filter.mentorId !== null) {
      params.append('mentorId', filter.mentorId.toString());
    }
    
    if (filter.isAnswered !== null) {
      params.append('isAnswered', filter.isAnswered.toString());
    }
    
    if (filter.fromDate && filter.fromDate.trim() !== '') {
      params.append('from', filter.fromDate);
    }
    
    if (filter.toDate && filter.toDate.trim() !== '') {
      params.append('to', filter.toDate);
    }

    if (filter.searchTerm && filter.searchTerm.trim() !== '') {
      params.append('search', filter.searchTerm.trim());
    }

    const fullUrl = `/api/admin/coffee-chats/list?${params.toString()}`;
    /// console.log('커피챗 질문글 목록 API 요청:', { fullUrl, apiBaseUrl: API_BASE_URL });

    try {
      // 환경변수를 사용한 올바른 URL로 요청
      const response = await fetch(`${API_BASE_URL}${fullUrl}`, {
        method: 'GET',
        headers: getHeaders(),
      });
      
      /// console.log('커피챗 질문글 목록 API 응답:', {response});
      
      if (!response.ok) {
        const errorText = await response.text();
        /// console.error('API 에러 응답:', errorText);
        throw new Error(`응답 애러 (${response.status}): ${errorText || response.statusText}`);
      }
      
      const data = await response.json();
      /// console.log('커피챗 질문글 목록 API 응답 데이터:', {data});
      
      return data;
    } catch (error) {
      /// console.error('API 호출 중 예외 발생:', error);
      throw error;
    }
  },

  // 커피챗 질문글 노출 상태 변경
  toggleQuestionVisibility: async (postId: number, visible: boolean): Promise<CoffeeChatQuestionActionResponse> => {
    const url = `${API_BASE_URL}/api/admin/coffee-chats/${postId}/visible?visible=${visible}`;
    /// console.log('노출 변경 요청 URL:', {url});
    
    const response = await fetch(url, {
      method: 'PATCH',
      headers: getHeaders(),
    });
    
    /// console.log('노출 변경 API 응답:', {response});
    
    if (!response.ok) {
      const errorText = await response.text();
      /// console.error('노출 변경 API 에러 응답:', errorText);
      throw new Error(`커피챗 질문글 노출 상태 변경 실패 (${response.status}): ${errorText || response.statusText}`);
    }
    
    const data = await response.json();
    /// console.log('커피챗 질문글 노출 상태 변경 성공:', {data});
    
    return data;
  },

  // 커피챗 질문글 삭제 (hard delete)
  deleteQuestion: async (postId: number): Promise<CoffeeChatQuestionDeleteResponse | void> => {
    const url = `${API_BASE_URL}/api/admin/coffee-chats/${postId}`;
    /// console.log('커피챗 질문글 삭제 요청:', {postId, url});
    
    const response = await fetch(url, {
      method: 'DELETE',
      headers: getHeaders(),
    });
    
    /// console.log('커피챗 질문글 삭제 API 응답:', {response});
    
    // 200 OK와 204 No Content 모두 성공으로 처리
    if (!response.ok && response.status !== 204) {
      const errorText = await response.text();
      /// console.error('삭제 API 에러 응답:', errorText);
      throw new Error(`커피챗 질문글 삭제 실패 (${response.status}): ${errorText || response.statusText}`);
    }
    
    // 204 No Content인 경우 빈 응답이므로 JSON 파싱하지 않음
    if (response.status === 204) {
      // /// console.log('커피챗 질문글 삭제 성공 (204 No Content)');
      return; // void 반환
    }
    
    // 200 OK인 경우 JSON 응답 처리
    const data = await response.json();
    // /// console.log('커피챗 질문글 삭제 성공 (200 OK):', data);
    
    return data;
  }
};

// 날짜 형식 검증 함수
const isValidDateFormat = (dateString: string): boolean => {
  const regex = /^\d{4}-\d{2}-\d{2}$/;
  if (!regex.test(dateString)) return false;
  
  const date = new Date(dateString);
  return date instanceof Date && !isNaN(date.getTime()) && dateString === formatToKSTDateString(date);
};
